import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ReportDatePickerValue,
  ReportDateRangeType,
  ReportDetail,
  AtaDashboardFilterOptions,
  ReportWidget,
} from '@vision/ui/interfaces';
import {
  DEFAULT_REPORT_DATE_FORMAT,
  LAST_7_DAYS_REPORT_DATE_PICKER_VALUE,
} from '@vision/ui/pages/ReportsDetailGraphic/constants';
import { ReportsDetailService } from '@vision/ui/services';
import { insertIfObject } from '@vision/ui/utils';
import dayjs from 'dayjs';

const getVoiceDateFormat = (date: string | undefined, defaultValue: string): string => {
  if (date && dayjs(date, DEFAULT_REPORT_DATE_FORMAT, true).isValid()) {
    return dayjs(date).format(DEFAULT_REPORT_DATE_FORMAT);
  }

  return defaultValue;
};

const DEFAULT_ATA_DASHBOARD_FILTER_OPTIONS: AtaDashboardFilterOptions = {
  from_date: LAST_7_DAYS_REPORT_DATE_PICKER_VALUE.fromDate,
  to_date: LAST_7_DAYS_REPORT_DATE_PICKER_VALUE.toDate,
  measure_value: 'feedback-count',
  content_category_ids: [],
  ai_model_ids: [],
  channel_ids: [],
  unit_ids: [],
  tag_groups: [],
  flow_ids: [],
};

function checkAtaDashboardFilters(detail?: AtaDashboardFilterOptions): AtaDashboardFilterOptions {
  if (!detail) {
    return DEFAULT_ATA_DASHBOARD_FILTER_OPTIONS;
  }

  const filteredDetail: AtaDashboardFilterOptions = {
    from_date: detail?.from_date ?? LAST_7_DAYS_REPORT_DATE_PICKER_VALUE.fromDate,
    to_date: detail?.to_date ?? LAST_7_DAYS_REPORT_DATE_PICKER_VALUE.toDate,
    measure_value: detail?.measure_value ?? 'feedback-count',
    channel_ids: Array.isArray(detail?.channel_ids) ? detail.channel_ids : [],
    unit_ids: Array.isArray(detail?.unit_ids) ? detail.unit_ids : [],
    flow_ids: Array.isArray(detail?.flow_ids) ? detail.flow_ids : [],
    content_category_ids: Array.isArray(detail?.content_category_ids) ? detail.content_category_ids : [],
    ai_model_ids: Array.isArray(detail?.ai_model_ids) ? detail.ai_model_ids : [],
    tag_groups: Array.isArray(detail?.tag_groups) ? detail.tag_groups : [],
  };

  return filteredDetail;
}

interface AtaDashboardStateType {
  ataDashboard?: ReportDetail;
  widgets?: ReportWidget[];
  selectedDate: ReportDatePickerValue;
  changedToDateRange: number;
  selectedMainFilters: AtaDashboardMainFilterType;
  widgetLoadStatus: {
    [key: string]: boolean;
  };
  isExporting: boolean;
  selectedWordCloudCategoryId?: string | null;
  showMoreRows?: boolean;
  comparison_from_date?: string;
  comparison_to_date?: string;
  detailContentCategoryIds?: string[];
  viewType?: 'list' | 'chart';
}

type AtaDashboardMainFilterType = {
  selectedChannelIds?: Array<string>;
  selectedUnitIds?: Array<string>;
  selectedFlowIds?: Array<string>;
  selectedContentCategoryIds: Array<string>;
  selectedAiModelIds?: Array<string>;
  selectedMeasureValue?: string;
};

const initialState: AtaDashboardStateType = {
  ataDashboard: null,
  widgets: [],
  selectedDate: LAST_7_DAYS_REPORT_DATE_PICKER_VALUE,
  changedToDateRange: 0,
  selectedMainFilters: {
    selectedChannelIds: [],
    selectedUnitIds: [],
    selectedFlowIds: [],
    selectedContentCategoryIds: [],
    selectedAiModelIds: [],
    selectedMeasureValue: 'feedback-count',
  },
  widgetLoadStatus: {},
  isExporting: false,
  selectedWordCloudCategoryId: null,
  showMoreRows: false,
  comparison_from_date: null,
  comparison_to_date: null,
  detailContentCategoryIds: [],
  viewType: 'list',
};

export const AtaDashboardState = createSlice({
  name: 'AtaDashboardState',
  initialState,
  reducers: {
    updateAtaDashboardDateRangeAction: (state, action) => {
      state.selectedDate = action.payload;
      if (state.ataDashboard && state.ataDashboard.detail) {
        state.ataDashboard.detail.from_date = action.payload.fromDate;
        state.ataDashboard.detail.to_date = action.payload.toDate;
      }
      state.changedToDateRange = state.changedToDateRange + 1;
    },

    updateAtaDashboardMainFiltersAction: (state, { payload }: PayloadAction<AtaDashboardMainFilterType>) => {
      if (!state.ataDashboard?.detail) {
        return;
      }

      const {
        selectedChannelIds,
        selectedUnitIds,
        selectedFlowIds,
        selectedContentCategoryIds,
        selectedAiModelIds,
        selectedMeasureValue,
      } = payload;

      // Update main filters state
      state.selectedMainFilters = {
        selectedChannelIds: selectedChannelIds ?? [],
        selectedUnitIds: selectedUnitIds ?? [],
        selectedFlowIds: selectedFlowIds ?? [],
        selectedContentCategoryIds: selectedContentCategoryIds ?? [],
        selectedAiModelIds: selectedAiModelIds ?? [],
        selectedMeasureValue: selectedMeasureValue ?? 'feedback-count',
      };

      Object.assign(state.ataDashboard.detail, {
        channel_ids: selectedChannelIds,
        unit_ids: selectedUnitIds,
        flow_ids: selectedFlowIds,
        content_category_ids: selectedContentCategoryIds,
        ai_model_ids: selectedAiModelIds,
        measure_value: selectedMeasureValue,
        tag_groups: state.ataDashboard.detail.tag_groups || [],
      });
    },

    // hem updateDateRangeAction i hemde  updateReportMainFiltersAction çalıştıran bir action
    updateAtaDashboardAllFiltersAction: (
      state,
      { payload }: PayloadAction<{ date: ReportDatePickerValue; filters: AtaDashboardMainFilterType }>,
    ) => {
      state.selectedDate = payload.date;
      state.selectedMainFilters = payload.filters;
    },

    setAtaDashboardChartFinishedAction: (state, { payload }: PayloadAction<string>) => {
      state.widgetLoadStatus[payload] = true;
    },
    setShowMoreRows: (state, {}: PayloadAction<boolean>) => {
      state.showMoreRows = !state.showMoreRows;
    },
    setComparisonDate: (
      state,
      { payload }: PayloadAction<{ comparison_from_date: string; comparison_to_date: string }>,
    ) => {
      state.comparison_from_date = payload.comparison_from_date;
      state.comparison_to_date = payload.comparison_to_date;
    },
    setDetailContentCategoryIds: (state, { payload }: PayloadAction<string[]>) => {
      state.detailContentCategoryIds = payload;
    },
    setViewType: (state, { payload }: PayloadAction<'list' | 'chart'>) => {
      state.viewType = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(ReportsDetailService.endpoints.getReport.matchFulfilled, (state, { payload }) => {
      const { detail } = payload.data;

      const selectedDate: ReportDatePickerValue = {
        ...LAST_7_DAYS_REPORT_DATE_PICKER_VALUE,
        ...insertIfObject(!!detail?.from_date, {
          fromDate: getVoiceDateFormat(detail?.from_date, LAST_7_DAYS_REPORT_DATE_PICKER_VALUE.fromDate),
        }),
        ...insertIfObject(!!detail?.to_date, {
          toDate: getVoiceDateFormat(detail?.to_date, LAST_7_DAYS_REPORT_DATE_PICKER_VALUE.toDate),
        }),
        ...insertIfObject(!!detail?.date_range, { dateRange: detail?.date_range as ReportDateRangeType }),
      };

      const normalizedDetail = checkAtaDashboardFilters(detail);

      Object.assign(state, {
        ataDashboard: {
          ...payload.data,
          latest_action_log: null, // not needed
          detail: normalizedDetail,
        },
        selectedDate,
        selectedMainFilters: {
          selectedContentCategoryIds: normalizedDetail.content_category_ids || [],
          selectedChannelIds: normalizedDetail.channel_ids || [],
          selectedUnitIds: normalizedDetail.unit_ids || [],
          selectedFlowIds: normalizedDetail.flow_ids || [],
          selectedAiModelIds: normalizedDetail.ai_model_ids || [],
          selectedMeasureValue: normalizedDetail.measure_value || 'feedback-count',
        },
      });
    });
    builder.addMatcher(ReportsDetailService.endpoints.getReportWidgets.matchFulfilled, (state, { payload }) => {
      state.widgets = payload.data.map((widget) => ({
        ...widget,
        detail: {
          ...widget.detail,
          from_date: getVoiceDateFormat(
            widget.detail?.from_date ? widget.detail.from_date : state.selectedDate.fromDate,
            LAST_7_DAYS_REPORT_DATE_PICKER_VALUE.fromDate,
          ),
          to_date: getVoiceDateFormat(
            widget.detail?.to_date ? widget.detail.to_date : state.selectedDate.toDate,
            LAST_7_DAYS_REPORT_DATE_PICKER_VALUE.toDate,
          ),
        },
      }));
      state.widgetLoadStatus = state.widgets.reduce(
        (acc, widget) => {
          acc[widget.id] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      );
    });
    builder.addMatcher(ReportsDetailService.endpoints.deleteReportWidget.matchFulfilled, (state, { payload }) => {
      state.widgets = state.widgets.filter((widget: ReportWidget) => widget.id !== payload.data.id); // Specify the type of widget
    });
    builder.addMatcher(ReportsDetailService.endpoints.updateReportWidget.matchFulfilled, (state, { payload }) => {
      state.widgets = state.widgets.map((widget: ReportWidget) => {
        if (widget.id === payload.data.id) {
          return {
            ...widget,
            ...payload.data,
          };
        }
        return widget;
      });
    });
    builder.addMatcher(ReportsDetailService.endpoints.copyReportWidget.matchFulfilled, (state, { payload, meta }) => {
      const copiedWidgetId = meta.arg.originalArgs?.widgetId;
      const copiedWidgetIndex = state.widgets.findIndex((widget: ReportWidget) => widget.id === copiedWidgetId);
      state.widgets.splice(copiedWidgetIndex + 1, 0, payload.data);
    });
  },
});

export const {
  updateAtaDashboardDateRangeAction,
  updateAtaDashboardMainFiltersAction,
  updateAtaDashboardAllFiltersAction,
  setAtaDashboardChartFinishedAction,
  setShowMoreRows,
  setComparisonDate,
  setDetailContentCategoryIds,
  setViewType,
} = AtaDashboardState.actions;
